import { useState, useEffect, useCallback } from "react";
import { RotateCcw, Check, X, ChevronLeft, ChevronRight } from "lucide-react";

export interface FlashcardItem {
  front: string;
  back: string;
}

interface FlashcardDeckProps {
  cards: FlashcardItem[];
  title?: string;
  onComplete?: (knewCount: number, totalCount: number) => void;
}

export function FlashcardDeck({ cards: initialCards, title, onComplete }: FlashcardDeckProps) {
  const [screen, setScreen] = useState<"study" | "results">("study");
  const [cards, setCards] = useState(initialCards);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [scores, setScores] = useState<Record<number, boolean>>({});
  const [anim, setAnim] = useState("");
  const [reviewMode, setReviewMode] = useState(false);

  useEffect(() => {
    setCards(initialCards);
    setIdx(0);
    setFlipped(false);
    setScores({});
    setReviewMode(false);
    setScreen("study");
  }, [initialCards]);

  const flip = useCallback(() => setFlipped((f) => !f), []);

  const markCard = (knew: boolean) => {
    const cardIdx = idx;
    setScores((s) => ({ ...s, [cardIdx]: knew }));
    setFlipped(false);
    setAnim(knew ? "correct" : "missed");
    setTimeout(() => {
      setAnim("");
      if (idx < cards.length - 1) {
        setIdx((i) => i + 1);
      } else {
        setScreen("results");
        const newScores = { ...scores, [cardIdx]: knew };
        const knewCount = Object.values(newScores).filter(Boolean).length;
        onComplete?.(knewCount, cards.length);
      }
    }, 400);
  };

  const nav = useCallback(
    (dir: "prev" | "next") => {
      if (dir === "prev" && idx > 0) {
        setFlipped(false);
        setIdx((i) => i - 1);
      }
      if (dir === "next" && idx < cards.length - 1) {
        setFlipped(false);
        setIdx((i) => i + 1);
      }
    },
    [idx, cards.length]
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (screen !== "study") return;
      if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === " ") {
        e.preventDefault();
        flip();
      }
      if (e.key === "ArrowLeft") nav("prev");
      if (e.key === "ArrowRight") nav("next");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [screen, flip, nav]);

  if (cards.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
        <p className="font-heading font-semibold">Flashcards</p>
        <p className="text-sm mt-1">No flashcards available for this module yet</p>
      </div>
    );
  }

  const knewCount = Object.values(scores).filter(Boolean).length;

  // RESULTS SCREEN
  if (screen === "results") {
    const total = cards.length;
    const pct = total > 0 ? Math.round((knewCount / total) * 100) : 0;
    const missedIndices = Object.entries(scores)
      .filter(([, v]) => v === false)
      .map(([k]) => Number(k));
    const missed = missedIndices.map((i) => cards[i]);

    const startReview = () => {
      if (missed.length === 0) return;
      setCards(missed);
      setIdx(0);
      setFlipped(false);
      setScores({});
      setReviewMode(true);
      setScreen("study");
    };

    return (
      <div className="rounded-2xl border bg-card p-8">
        <div className="text-center">
          <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl ${pct >= 80 ? "bg-green-100" : "bg-primary/10"}`}>
            {pct >= 80 ? "🏆" : "📚"}
          </div>
          <h3 className="font-heading text-2xl font-bold text-foreground mb-1">
            {pct >= 80 ? "Excellent!" : pct >= 50 ? "Good progress!" : "Keep going!"}
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            {reviewMode ? "Review round" : title || "Flashcards"}
          </p>

          <div className="text-5xl font-heading font-bold text-primary mb-1">{pct}%</div>
          <p className="text-sm text-muted-foreground mb-6">
            {knewCount} of {total} cards mastered
          </p>

          <div className="flex justify-center gap-8 mb-6">
            <div className="text-center">
              <div className="font-heading text-2xl font-bold text-green-600">{knewCount}</div>
              <div className="text-sm text-muted-foreground">Got it</div>
            </div>
            <div className="text-center">
              <div className="font-heading text-2xl font-bold text-orange-500">{missed.length}</div>
              <div className="text-sm text-muted-foreground">Still learning</div>
            </div>
          </div>

          {missed.length > 0 && (
            <div className="mb-6 rounded-xl bg-muted/50 p-4 text-left">
              <p className="text-sm text-muted-foreground mb-2">Cards to review:</p>
              <div className="space-y-1.5">
                {missed.map((c, i) => (
                  <div key={i} className="rounded-lg bg-background text-sm p-2 px-3">
                    {c.front}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            {missed.length > 0 && (
              <button
                onClick={startReview}
                className="w-full rounded-full bg-[var(--color-acrrm-dark)] text-white text-sm font-medium py-2.5 px-5 hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Review missed cards
              </button>
            )}
            <button
              onClick={() => {
                setCards(initialCards);
                setIdx(0);
                setFlipped(false);
                setScores({});
                setReviewMode(false);
                setScreen("study");
              }}
              className="w-full rounded-full bg-muted text-foreground text-sm font-medium py-2.5 px-5 hover:bg-muted/80 transition-colors"
            >
              Start over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // STUDY SCREEN
  const card = cards[idx];

  return (
    <div className="rounded-2xl border bg-card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {reviewMode ? "Review Mode" : title || "Flashcards"}
        </p>
        <p className="text-sm font-medium text-foreground">
          {idx + 1} / {cards.length}
        </p>
      </div>

      {/* Progress bar */}
      <div className="h-1 rounded-full overflow-hidden mb-6 bg-muted">
        <div
          className="h-full rounded-full transition-all duration-500 bg-[var(--color-acrrm-dark)]"
          style={{ width: `${((idx + 1) / cards.length) * 100}%` }}
        />
      </div>

      {/* Card with flip animation */}
      <div
        onClick={flip}
        className={`relative w-full cursor-pointer transition-all duration-200 ${
          anim ? "scale-95 opacity-70" : "hover:scale-[1.005]"
        }`}
        style={{ perspective: "1000px", minHeight: "240px" }}
      >
        <div
          className="relative w-full transition-transform duration-500"
          style={{
            transformStyle: "preserve-3d",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
            minHeight: "240px",
          }}
        >
          {/* Front */}
          <div
            className="absolute inset-0 rounded-2xl shadow-lg flex flex-col items-center justify-center p-8 border bg-background"
            style={{ backfaceVisibility: "hidden" }}
          >
            <h2 className="text-center font-heading text-xl md:text-2xl font-bold text-foreground">
              {card.front}
            </h2>
            <p className="text-sm text-muted-foreground mt-4">Tap to reveal</p>
          </div>

          {/* Back */}
          <div
            className="absolute inset-0 rounded-2xl shadow-lg flex flex-col items-center justify-center p-8 border bg-background"
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
          >
            <p className="text-center text-base leading-relaxed text-foreground">
              {card.back}
            </p>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-center gap-3 mt-6">
        <button
          onClick={() => nav("prev")}
          disabled={idx === 0}
          className="p-2 rounded-full border bg-muted/50 text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => markCard(false)}
          className="flex items-center gap-2 border rounded-full bg-muted/50 text-foreground text-sm font-medium py-2 px-5 hover:bg-orange-50 hover:border-orange-200 transition-colors"
        >
          <X className="h-4 w-4" />
          Still learning
        </button>
        <button
          onClick={() => markCard(true)}
          className="flex items-center gap-2 border rounded-full bg-muted/50 text-foreground text-sm font-medium py-2 px-5 hover:bg-green-50 hover:border-green-200 transition-colors"
        >
          <Check className="h-4 w-4" />
          Got it
        </button>
        <button
          onClick={() => nav("next")}
          disabled={idx === cards.length - 1}
          className="p-2 rounded-full border bg-muted/50 text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Keyboard hint */}
      <p className="text-center text-xs text-muted-foreground/50 mt-3">
        Arrow keys to navigate &middot; Space to flip
      </p>
    </div>
  );
}
